import { Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import type { EvaluationReportForMathAndMathComputerScience } from '../../../Types/interfaces';
import {
  getDocumentationGrade,
  getGainedKnowledgeGrade,
  getKnowledgeGrade,
} from '../utils/calculations';
import type { JSX } from 'react';

const { useBreakpoint } = Grid;

interface MathAndMathComputerScienceEvaluationReportDisplayComponentProps {
  evaluationReport: EvaluationReportForMathAndMathComputerScience;
}

export const MathAndMathComputerScienceEvaluationReportDisplayComponent = ({
  evaluationReport,
}: MathAndMathComputerScienceEvaluationReportDisplayComponentProps): JSX.Element => {
  const { t } = useTranslation();
  const screens = useBreakpoint();

  if (!evaluationReport) {
    return <div>{t('evalReportDisplay.notFound')}</div>;
  }

  return (
    <Descriptions
      title={t('evalReportDisplay.specKnowledge')}
      bordered
      column={screens.xl ? 2 : 1}
      layout={screens.xs ? 'vertical' : 'horizontal'}
      labelStyle={
        screens.xs
          ? {
              fontWeight: 700,
            }
          : {}
      }
    >
      {/* this section of code is common with ComputerScienceEvaluationReportDisplayComponent,
        because the ant design didn't allow to use MathAndMathComputerScienceEvalutaionReportDisplayComponent inside
        the parent descriptions component, so I had to duplicate the code */}
      <Descriptions.Item label={t('evalReportDisplay.knowledgeAtStart')}>
        {getKnowledgeGrade(evaluationReport.studentsKnowledgeWhenStarting, t)}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.qoa')}>
        {getKnowledgeGrade(evaluationReport.qualityOfWork, t)}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.qod')}>
        {getDocumentationGrade(evaluationReport.qualityOfDocumentation, t)}
      </Descriptions.Item>

      <Descriptions.Item
        label={t('evalReportDisplay.math.mathematicalStructures')}
      >
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForFundamentalMathematicalStructures,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item
        label={t(
          'evalReportDisplay.math.algebraAnalysisGeometryAndDifferentialEquations'
        )}
      >
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForAlgebraAnalysisGeometryAndDifferentialEquations,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item
        label={t('evalReportDisplay.math.mathematicaModeling')}
      >
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForMathematicalModeling,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item
        label={t('evalReportDisplay.math.probabilityAndStatistics')}
      >
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForProbabilityAndStatistics,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item
        label={t('evalReportDisplay.math.teachingMethodologyAndDidactics')}
      >
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForTeachingMethodologyAndDidactics,
          t
        )}
      </Descriptions.Item>

      {evaluationReport.pointsForOtherMathematicalSkills !== 0 && (
        <Descriptions.Item
          label={
            t('evalReportDisplay.other') +
            ': ' +
            evaluationReport.otherDevelopmentSkills
          }
        >
          {getGainedKnowledgeGrade(
            evaluationReport.pointsForOtherMathematicalSkills,
            t
          )}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};
